import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { extractionApi, ExtractionDataTransformer, ExtractionApiError } from '../services/extractionApi';
import { useMultiExtraction } from './useMultiExtraction';
import { ExtractionResponse, ExtractionResult } from '../types/icd10';
import { ExtractionSession } from '../types/multi-extraction';

export interface UseExtractionsResult {
  // Active extractions (from useMultiExtraction)
  activeSessions: ExtractionSession[];
  selectedSession: ExtractionSession | null;
  activeSession: ExtractionSession | null;

  // Saved extractions
  savedSessions: ExtractionSession[];
  savedExtractions: ExtractionResult[];

  // Loading states
  isLoadingActive: boolean;
  isLoadingSaved: boolean;
  isSyncingSaved: boolean;

  // Errors
  activeError: string | null;
  savedError: string | null;

  // Actions
  selectSession: (id: string | null) => void;
  deleteActiveSession: (id: string) => void;
  cancelActiveSession: (id: string) => void;
  updateSessionTitle: (id: string, title: string) => void;
  deleteSavedSession: (id: string) => Promise<void>;
  updateSavedFeedback: (id: string, feedback: string) => Promise<void>;
  refreshSavedExtractions: () => Promise<void>;
  waitForSavedExtraction: (backendRequestId: string, opts?: { timeoutMs?: number; intervalMs?: number }) => Promise<boolean>;
  waitForSavedExtractionAbortable: (backendRequestId: string, opts?: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal }) => Promise<boolean>;
  upsertSavedExtraction: (payload: { response: ExtractionResponse; session?: ExtractionSession | null; originalText?: string | null }) => void;
}

/**
 * Comprehensive hook for managing both active and saved extractions
 */
export function useExtractions(): UseExtractionsResult {
  const { isAuthenticated, isLoading: isAuthLoading, logout } = useAuth();

  // Active extractions (in-memory, real-time)
  const multiExtraction = useMultiExtraction();
  const { actions, selectors } = multiExtraction;

  // Saved extractions (from backend)
  const [savedExtractions, setSavedExtractions] = useState<ExtractionResult[]>([]);
  const [savedSessions, setSavedSessions] = useState<ExtractionSession[]>([]);
  const [isLoadingSaved, setIsLoadingSaved] = useState(false);
  const [isSyncingSaved, setIsSyncingSaved] = useState(false);
  const [savedError, setSavedError] = useState<string | null>(null);
  const isLoadingRef = useRef(false);
  const savedExtractionsRef = useRef<ExtractionResult[]>([]);

  // Convert saved extractions to session format
  useEffect(() => {
    const sessions = ExtractionDataTransformer.savedExtractionsToSessions(savedExtractions);
    setSavedSessions(sessions);
  }, [savedExtractions]);

  useEffect(() => {
    savedExtractionsRef.current = savedExtractions;
  }, [savedExtractions]);

  // Load saved extractions when authenticated
  const loadSavedExtractions = useCallback(async (): Promise<ExtractionResult[] | null> => {
    if (!isAuthenticated || isAuthLoading || isLoadingRef.current) return null;

    const token = localStorage.getItem('bearer_token');
    if (!token) return null; // Wait for token

    try {
      isLoadingRef.current = true;
      setIsLoadingSaved(true);
      setSavedError(null);
      const extractions = await extractionApi.fetchSavedExtractions();
      savedExtractionsRef.current = extractions;
      setSavedExtractions(extractions);
      return extractions;
    } catch (error) {
      const apiError = error as ExtractionApiError;
      console.error('Failed to load saved extractions:', error);
      if (apiError.code === 'UNAUTHORIZED') {
        // Token expired, logout user
        logout();
        setSavedError('Session expired. Please log in again.');
      } else {
        const messageParts = [apiError.message || 'Failed to load saved extractions'];
        if (apiError.code) {
          messageParts.push(`(code: ${apiError.code})`);
        }
        if (apiError.code === 'INVALID_RESPONSE' || apiError.code === 'INVALID_JSON') {
          messageParts.push('See console for backend payload details.');
        }
        setSavedError(messageParts.join(' '));
      }
      return null;
    } finally {
      setIsLoadingSaved(false);
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, isAuthLoading, logout]);

  // Load saved extractions on mount and when auth changes
  useEffect(() => {
    loadSavedExtractions();
  }, [loadSavedExtractions]);

  // Delete saved extraction
  const deleteSavedSession = useCallback(async (id: string) => {
    try {
      await extractionApi.deleteSavedExtraction(id);
      setSavedExtractions(prev => {
        const next = prev.filter(ext => ext.request_id !== id);
        savedExtractionsRef.current = next;
        return next;
      });
    } catch (error) {
      const apiError = error as ExtractionApiError;
      if (apiError.code === 'UNAUTHORIZED') {
        logout();
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(apiError.message || 'Failed to delete extraction');
    }
  }, [logout]);

  // Update saved extraction feedback
  const updateSavedFeedback = useCallback(async (id: string, feedback: string) => {
    try {
      await extractionApi.updateExtractionFeedback(id, feedback);
      setSavedExtractions(prev => {
        const next = prev.map(ext =>
          ext.request_id === id
            ? { ...ext, user_feedback: feedback }
            : ext
        );
        savedExtractionsRef.current = next;
        return next;
      });
    } catch (error) {
      const apiError = error as ExtractionApiError;
      if (apiError.code === 'UNAUTHORIZED') {
        logout();
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(apiError.message || 'Failed to update feedback');
    }
  }, [logout]);

  // Refresh saved extractions
  const refreshSavedExtractions = useCallback(async () => {
    await loadSavedExtractions();
  }, [loadSavedExtractions]);

  // Poll for a specific saved extraction to appear
  const waitForSavedExtraction = useCallback(async (backendRequestId: string, opts?: { timeoutMs?: number; intervalMs?: number }) => {
    const timeoutMs = opts?.timeoutMs ?? 10000;
    const intervalMs = opts?.intervalMs ?? 800;
    const start = Date.now();
    setIsSyncingSaved(true);
    try {
      if (savedExtractionsRef.current.some(e => e.request_id === backendRequestId)) return true;
      while (Date.now() - start < timeoutMs) {
        try { await loadSavedExtractions(); } catch (_) { /* ignore transient */ }
        if (savedExtractionsRef.current.some(e => e.request_id === backendRequestId)) return true;
        await new Promise(res => setTimeout(res, intervalMs));
      }
      return savedExtractionsRef.current.some(e => e.request_id === backendRequestId);
    } finally {
      setIsSyncingSaved(false);
    }
  }, [loadSavedExtractions]);

  const waitForSavedExtractionAbortable = useCallback(async (backendRequestId: string, opts?: { timeoutMs?: number; intervalMs?: number; signal?: AbortSignal }) => {
    const timeoutMs = opts?.timeoutMs ?? 10000;
    const intervalMs = opts?.intervalMs ?? 800;
    const start = Date.now();
    const signal = opts?.signal;
    setIsSyncingSaved(true);
    try {
      if (savedExtractionsRef.current.some(e => e.request_id === backendRequestId)) return true;
      while (Date.now() - start < timeoutMs) {
        if (signal?.aborted) throw new Error('aborted');
        try { await loadSavedExtractions(); } catch (_) { /* ignore */ }
        if (savedExtractionsRef.current.some(e => e.request_id === backendRequestId)) return true;
        await new Promise((res, rej) => {
          const timer = setTimeout(res, intervalMs);
          if (signal) {
            const onAbort = () => { clearTimeout(timer); rej(new Error('aborted')); };
            signal.addEventListener('abort', onAbort, { once: true });
          }
        });
      }
      return savedExtractionsRef.current.some(e => e.request_id === backendRequestId);
    } finally {
      setIsSyncingSaved(false);
    }
  }, [loadSavedExtractions]);

  const upsertSavedExtraction = useCallback(({ response, session, originalText }: { response: ExtractionResponse; session?: ExtractionSession | null; originalText?: string | null }) => {
    const requestId = response.request_id || session?.backendRequestId || session?.id;
    if (!requestId) return;

    const mergedExtraJson = (() => {
      const meta = response.metadata ? { metadata: response.metadata } : {};
      const pcsPayload = response.pcs ? { pcs: response.pcs } : {};
      const flags = {
        ran_diagnostic: response.ran_diagnostic ?? session?.results?.ran_diagnostic ?? undefined,
        ran_pcs: response.ran_pcs ?? session?.results?.ran_pcs ?? undefined,
      };
      const combined = { ...meta, ...pcsPayload, ...flags } as Record<string, unknown>;
      return Object.keys(combined).length > 0 ? combined : null;
    })();

    const nextExtraction: ExtractionResult = {
      request_id: requestId,
      input_text: session?.originalText ?? session?.request?.text ?? originalText ?? '',
      codes: response.codes,
      user_feedback: session?.feedback,
      created_at: Date.now(),
      duration_seconds: response.processing_time ?? response.metadata?.processing_time ?? null,
      extra_json: mergedExtraJson,
    };

    setSavedExtractions(prev => {
      const existingIndex = prev.findIndex(ext => ext.request_id === requestId);
      let next: ExtractionResult[];
      if (existingIndex >= 0) {
        const existing = prev[existingIndex];
        const mergedExtraJsonObject = {
          ...(existing.extra_json && typeof existing.extra_json === 'object'
            ? existing.extra_json
            : {}),
          ...(nextExtraction.extra_json && typeof nextExtraction.extra_json === 'object'
            ? nextExtraction.extra_json
            : {}),
        } as Record<string, unknown>;
        const normalizedExtraJson = Object.keys(mergedExtraJsonObject).length > 0
          ? mergedExtraJsonObject
          : null;
        const merged: ExtractionResult = {
          ...existing,
          ...nextExtraction,
          extra_json: normalizedExtraJson,
        };
        next = [...prev];
        next[existingIndex] = merged;
      } else {
        next = [nextExtraction, ...prev];
      }
      savedExtractionsRef.current = next;
      return next;
    });
  }, []);

  return {
    // Active extractions
    activeSessions: selectors.getAllSessions(),
    selectedSession: selectors.getSelectedSession(),
    activeSession: selectors.getActiveSession(),

    // Saved extractions
    savedSessions,
    savedExtractions,

    // Loading states
    isLoadingActive: false, // useMultiExtraction doesn't expose loading state
    isLoadingSaved,
    isSyncingSaved,

    // Errors
    activeError: null, // useMultiExtraction doesn't expose error state
    savedError,

    // Actions
    selectSession: actions.selectSession,
    deleteActiveSession: actions.deleteSession,
    cancelActiveSession: actions.cancelExtraction,
    updateSessionTitle: actions.updateTitle,
    deleteSavedSession,
    updateSavedFeedback,
    refreshSavedExtractions,
    waitForSavedExtraction,
    waitForSavedExtractionAbortable,
    upsertSavedExtraction,
  };
}
